import React, { useCallback, useEffect, useRef, useState } from "react";
import classNames from "classnames";
import { Link } from "react-router-dom";

import CongratsLeft from "../../../../../../assets/resultLeftImages.svg";
import CongratsRight from "../../../../../../assets/resultRightImages.png";
import { Success } from "../../../../../../modals";
import SuccessImage from "../../../../../../assets/success.png";
import useWindowWidth from "../../../../../../hooks/useWindowWidth";
import SortScoopCard from "./SortScoopCard";
import "./AiResult.scss";
import "./SortScoopCard.scss";

[CongratsLeft, CongratsRight].forEach((src) => {
  const preload = new Image();
  preload.src = src;
});

const STORAGE_KEY = "career-sort-decisions";

function loadStoredDecisions() {
  try {
    return JSON.parse(sessionStorage.getItem(STORAGE_KEY) || "{}") || {};
  } catch {
    return {};
  }
}

function storeDecisions(next) {
  sessionStorage.setItem(STORAGE_KEY, JSON.stringify(next));
}

function careerTitle(item) {
  const title = item?.unique_title;
  if (!title) return item?.text || "";
  if (typeof title === "string") return title;
  return title.mask_title || title.title || item?.text || "";
}

function decisionFor(item, decisions, ignoreStored = false) {
  if (decisions[item?.id]) return decisions[item.id];
  if (ignoreStored) return null;
  if (item?.sort_decision === "save" || item?.sort_decision === "maybe" || item?.sort_decision === "skip") {
    return item.sort_decision;
  }
  if (item?.interested) return "save";
  return null;
}

const AiResult = ({
  data,
  isModalOpened,
  handleModalOpen,
  closeModal,
  handleSaveCareer: _handleSaveCareer,
  persistScore,
  persistSortDecision,
  chosenItem,
  handlescore,
  isSavedCareer: _isSavedCareer,
  handleBook: _handleBook,
  isSuccessModalOpened,
  closeSuccessModal,
  selectedItems: _selectedItems,
  showLoader: _showLoader,
}) => {
  const { isMobile } = useWindowWidth();
  const [screen, setScreen] = useState("intro");
  const [sequential, setSequential] = useState(false);
  const [decisions, setDecisions] = useState(() => {
    const initial = { ...loadStoredDecisions() };
    (data || []).forEach((career) => {
      const stored = decisionFor(career, {});
      if (stored) initial[career.id] = stored;
    });
    return initial;
  });
  const [countUp, setCountUp] = useState(0);
  const [rating, setRating] = useState(3);
  const [dragX, setDragX] = useState(0);
  const [dragging, setDragging] = useState(false);
  const [exitDirection, setExitDirection] = useState(null);
  const committing = useRef(false);
  const startX = useRef(0);
  const ignoreStoredRatings = useRef(false);
  const [imagesLoaded, setImagesLoaded] = useState({
    congratsLeft: false,
    congratsRight: false,
  });

  const total = data?.length || 0;
  const careers = data || [];

  useEffect(() => {
    if (ignoreStoredRatings.current) return;
    setDecisions((prev) => {
      const next = { ...prev };
      (data || []).forEach((career) => {
        const stored = decisionFor(career, {});
        if (stored && !next[career.id]) next[career.id] = stored;
      });
      return next;
    });
  }, [data]);

  useEffect(() => {
    if (screen !== "intro") return undefined;
    setCountUp(0);
    let n = 0;
    const id = setInterval(() => {
      n += 1;
      setCountUp(n);
      if (n >= total) clearInterval(id);
    }, 65);
    return () => clearInterval(id);
  }, [screen, total]);

  useEffect(() => {
    const existing = Number(chosenItem?.score);
    setRating(Number.isFinite(existing) && existing > 0 ? existing : 3);
  }, [chosenItem?.id, chosenItem?.score]);

  // Only reset the fly animation when the card changes. Saving a score on an
  // unrated career updates chosenItem.score and must not cancel the exit.
  useEffect(() => {
    setDragX(0);
    setExitDirection(null);
  }, [chosenItem?.id]);

  const markArtLoaded = (imageName) => {
    setImagesLoaded((prev) => (prev[imageName] ? prev : { ...prev, [imageName]: true }));
  };

  const bindArtImg = (imageName) => (img) => {
    if (img && img.complete) markArtLoaded(imageName);
  };

  const applyDecision = useCallback(
    (decision) => {
      if (!chosenItem?.id || committing.current) return;
      committing.current = true;
      const updated = { ...decisions, [chosenItem.id]: decision };
      setDecisions(updated);
      storeDecisions(updated);
      persistScore?.(chosenItem.id, rating);
      handlescore?.(rating);
      persistSortDecision?.(chosenItem.id, decision);
      setExitDirection(decision === "save" ? "right" : decision === "skip" ? "left" : "down");

      const remaining = (data || []).filter(
        (item) => item.id !== chosenItem.id && !updated[item.id]
      );
      window.setTimeout(() => {
        committing.current = false;
        setExitDirection(null);
        setDragX(0);
        if (sequential && remaining.length) {
          handleModalOpen(remaining[0].id);
          return;
        }
        setSequential(false);
        closeModal?.();
        setScreen(!remaining.length ? "done" : "grid");
      }, 380);
    },
    [
      chosenItem,
      decisions,
      data,
      sequential,
      rating,
      persistScore,
      persistSortDecision,
      handlescore,
      handleModalOpen,
      closeModal,
    ]
  );

  const startSequential = () => {
    const firstUndecided = careers.find((item) => !decisions[item.id]);
    if (!firstUndecided) {
      closeModal?.();
      setScreen(careers.length ? "done" : "intro");
      return;
    }
    setSequential(true);
    setScreen("sorting");
    handleModalOpen(firstUndecided.id);
  };

  const startOver = () => {
    ignoreStoredRatings.current = true;
    setDecisions({});
    storeDecisions({});
    setSequential(false);
    closeModal?.();
    setScreen("intro");
  };

  const ratingFor = (item) => decisionFor(item, decisions, ignoreStoredRatings.current);

  const openFromGrid = (id) => {
    setSequential(false);
    handleModalOpen(id);
  };

  const onPointerDown = (event) => {
    if (!isMobile || screen !== "sorting") return;
    if (event.target.closest("button, a, input")) return;
    setDragging(true);
    startX.current = event.clientX ?? event.touches?.[0]?.clientX ?? 0;
  };

  const onPointerMove = (event) => {
    if (!dragging) return;
    const x = event.clientX ?? event.touches?.[0]?.clientX ?? 0;
    setDragX(x - startX.current);
  };

  const onPointerUp = () => {
    if (!dragging) return;
    setDragging(false);
    if (dragX > 100) applyDecision("save");
    else if (dragX < -100) applyDecision("skip");
    else setDragX(0);
  };

  const sortedCount = careers.filter((item) => decisions[item.id]).length;
  const savedList = careers.filter((item) => ratingFor(item) === "save");
  const maybeList = careers.filter((item) => ratingFor(item) === "maybe");
  const nextCareer = careers.find(
    (item) => item.id !== chosenItem?.id && !decisions[item.id]
  );

  const boxClass = (item, extra) =>
    classNames(extra, {
      "AiResult__box--save": ratingFor(item) === "save",
      "AiResult__box--maybe": ratingFor(item) === "maybe",
      "AiResult__box--skip": ratingFor(item) === "skip",
    });

  const sortPriorityItems = (arr) => {
    const matched = [];
    const others = [];

    for (const item of arr) {
      if (item?.ai?.includes("AI matched your preference")) {
        matched.push(item);
      } else {
        others.push(item);
      }
    }

    const firstRowSize = Math.ceil(arr.length / 2);
    const firstRow = new Array(firstRowSize);
    const center = Math.floor(firstRowSize / 2);

    let left = 1;
    let right = 1;

    matched.forEach((item, index) => {
      if (index === 0) firstRow[center] = item;
      else if (index % 2 !== 0) firstRow[center - left++] = item;
      else firstRow[center + right++] = item;
    });

    let otherIndex = 0;

    for (let i = 0; i < firstRow.length; i++) {
      if (!firstRow[i]) {
        firstRow[i] = others[otherIndex++];
      }
    }

    return [...firstRow, ...others.slice(otherIndex)];
  };

  const renderStar = (item) => {
    const saved = ratingFor(item) === "save";
    return (
      <span className="career-star" aria-hidden="true">
        <svg viewBox="0 0 32 36" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path
            d="M5 2h22a3 3 0 0 1 3 3v29.2l-14-7.4-14 7.4V5a3 3 0 0 1 3-3Z"
            fill="#5FE3C4"
          />
          <path
            d="M16 10.2 17.6 14l4.2.4-3.2 2.8.9 4.1L16 19.2 12.5 21.3l.9-4.1-3.2-2.8 4.2-.4L16 10.2Z"
            fill={saved ? "#F6C445" : "#FFFFFF"}
          />
        </svg>
      </span>
    );
  };

  const bothImagesLoaded = imagesLoaded.congratsLeft && imagesLoaded.congratsRight;

  const drwaItems = (arr) => {
    arr = sortPriorityItems(arr);

    const result = [];
    for (let i = 0; i < Math.round(arr.length / 2); i++) {
      if (i + 1 < arr.length || i + 1 === arr.length) {
        const second = arr[i + Math.round(arr.length / 2)];
        result.push(
          <div className="AiResult__answerCont__column" key={arr[i]?.id || i}>
            <div className="AiResult__answerCont__column__cont">
              <div
                className={boxClass(arr[i], "AiResult__answerCont__column__cont__startPart")}
                onClick={() => openFromGrid(arr[i].id)}
              >
                {renderStar(arr[i])}
                <p className="AiResult__answerCont__column__cont__startPart__text">
                  {careerTitle(arr[i])}
                </p>
              </div>
              <p
                className={classNames(
                  "AiResult__answerCont__column__cont__endPartGreen",
                  {
                    darekYellow: arr[i]?.ai?.includes("AI recommended"),
                  }
                )}
              >
                {arr[i].ai}
              </p>
            </div>
            {second ? (
              <div className="AiResult__answerCont__column__cont">
                <div
                  className={boxClass(second, "AiResult__answerCont__column__cont__startPart")}
                  onClick={() => openFromGrid(second.id)}
                >
                  {renderStar(second)}
                  <p className="AiResult__answerCont__column__cont__startPart__text">
                    {careerTitle(second)}
                  </p>
                </div>
                <p
                  className={classNames(
                    "AiResult__answerCont__column__cont__endPartGreen",
                    {
                      darekYellow: second?.ai?.includes("AI recommended"),
                    }
                  )}
                >
                  {second.ai}
                </p>
              </div>
            ) : null}
          </div>
        );
      }
    }
    return result;
  };

  const drawItemsForMibile = (arr) => {
    return arr.map((el) => (
      <div className="AiResult__answerCont__columns" key={el.id}>
        <div
          className={boxClass(el, "AiResult__answerCont__columns__cont")}
          onClick={() => openFromGrid(el.id)}
        >
          {renderStar(el)}
          <p className="AiResult__answerCont__columns__cont__text">{careerTitle(el)}</p>
        </div>
        <p
          className={classNames("AiResult__answerCont__columns__ai", {
            darekYellow: el?.ai?.includes("AI recommended"),
          })}
        >
          {el?.ai}
        </p>
      </div>
    ));
  };

  const sortCaption = chosenItem?.id
    ? `Scoop ${(data || []).findIndex((item) => item.id === chosenItem.id) + 1} of ${total}`
    : "";

  const cones = (
    <div
      className={classNames("AiResult__bottomArt", {
        "AiResult__bottomArt--ready": bothImagesLoaded,
      })}
      aria-hidden="true"
    >
      <img
        src={CongratsLeft}
        alt=""
        className={classNames("AiResult__CongratsLeft", {
          unvisibleCongrats: isMobile,
        })}
        ref={bindArtImg("congratsLeft")}
        onLoad={() => markArtLoaded("congratsLeft")}
        onError={() => markArtLoaded("congratsLeft")}
      />
      <img
        src={CongratsRight}
        alt=""
        className={classNames("AiResult__CongratsRight", {
          unvisibleCongrats: isMobile,
        })}
        ref={bindArtImg("congratsRight")}
        onLoad={() => markArtLoaded("congratsRight")}
        onError={() => markArtLoaded("congratsRight")}
      />
    </div>
  );

  return (
    <>
      <div>
        {screen === "intro" ? (
          <div className="AiResult AiResult--intro">
            <h1 className="AiResult__introTitle">
              {countUp} Career Scoop{countUp === 1 ? "" : "s"}, Ready for You!
            </h1>
            <p className="AiResult__introBody">
              Based on a unique blend of things about you, we put these {total} careers together.
            </p>
            <div className="AiResult__introActions">
              <button type="button" className="AiResult__introPrimary" onClick={startSequential}>
                View a Scoop at a Time
              </button>
              <button type="button" className="AiResult__introSecondary" onClick={() => setScreen("grid")}>
                Peek at All {total}
              </button>
            </div>
            {cones}
          </div>
        ) : screen === "done" ? (
          <div className="AiResult AiResult--intro AiResult--done">
            <h1 className="AiResult__introTitle">
              {savedList.length > 0 ? "Great! Here's your shortlist" : "All sorted!"}
            </h1>
            {savedList.length === 0 ? (
              <p className="AiResult__introBody">
                You skipped through all {total}. Want to take another look at your maybe pile?
              </p>
            ) : (
              <div className="AiResult__doneBlock">
                <p className="AiResult__doneLabel">Your Careers of Interest</p>
                <div className="AiResult__doneList">
                  {savedList.map((item) => (
                    <div className="AiResult__doneItem" key={item.id}>
                      {careerTitle(item)}
                    </div>
                  ))}
                </div>
              </div>
            )}
            {maybeList.length > 0 ? (
              <div className="AiResult__doneBlock">
                <p className="AiResult__doneLabel">Careers to Consider ({maybeList.length})</p>
                <div className="AiResult__doneMaybes">
                  {maybeList.map((item) => (
                    <span className="AiResult__doneChip" key={item.id}>
                      {careerTitle(item)}
                    </span>
                  ))}
                </div>
              </div>
            ) : null}
            <div className="AiResult__doneActions">
              <Link className="AiResult__introPrimary AiResult__donePrimary" to="/profile/recommendations">
                View these in my profile
              </Link>
              <Link className="AiResult__introSecondary AiResult__doneSecondary" to="/profile/action-plan">
                Build a Plan
              </Link>
              <Link className="AiResult__introSecondary AiResult__doneSecondary" to="/profile/strengths">
                View My Strengths
              </Link>
              <button type="button" className="AiResult__doneStartOver" onClick={startOver}>
                Start over
              </button>
            </div>
            {cones}
          </div>
        ) : screen === "sorting" && chosenItem?.id ? (
          <div className="AiResult AiResult--intro">
            <div className="AiResult__sorting">
              <button
                type="button"
                className="AiResult__viewAll"
                onClick={() => {
                  setSequential(false);
                  closeModal?.();
                  setScreen("grid");
                }}
              >
                View all {total}
              </button>
              <div className="AiResult__progress">
                <div className="AiResult__progressMeta">
                  <span>{sortCaption}</span>
                  <span>{sortedCount} done</span>
                </div>
                <div className="AiResult__progressTrack">
                  <div
                    className="AiResult__progressFill"
                    style={{ width: `${total ? Math.min(100, (sortedCount / total) * 100) : 0}%` }}
                  />
                </div>
              </div>
              <div
                className="AiResult__deck"
                onMouseDown={onPointerDown}
                onMouseMove={onPointerMove}
                onMouseUp={onPointerUp}
                onMouseLeave={() => dragging && onPointerUp()}
                onTouchStart={onPointerDown}
                onTouchMove={onPointerMove}
                onTouchEnd={onPointerUp}
              >
                {nextCareer ? (
                  <div className="AiResult__peek">
                    <SortScoopCard peek career={nextCareer} title={careerTitle(nextCareer)} />
                  </div>
                ) : null}
                <div style={{ position: "relative", zIndex: 1 }}>
                  <SortScoopCard
                    career={chosenItem}
                    title={careerTitle(chosenItem)}
                    text={chosenItem?.description}
                    rating={rating}
                    onRatingChange={(value) => {
                      setRating(value);
                      handlescore?.(value);
                    }}
                    onDecide={applyDecision}
                    dragX={dragX}
                    dragging={dragging}
                    exitDirection={exitDirection}
                  />
                </div>
              </div>
              {isMobile ? (
                <p className="AiResult__swipeHint">You can also swipe the card: right to save, left to skip</p>
              ) : null}
            </div>
            {cones}
          </div>
        ) : (
          <div className="AiResult">
            <p className="AiResult__title">
              Here are your top 10 career scoops click to learn more
            </p>
            <button type="button" className="AiResult__introPrimary AiResult__sortCta" onClick={startSequential}>
              View a Scoop at a Time
            </button>
            <div className="AiResult__answerCont">
              {isMobile ? drawItemsForMibile(careers) : drwaItems(careers)}
            </div>
            {sortedCount > 0 ? (
              <button
                type="button"
                className="AiResult__answerCont__link"
                onClick={() => setScreen("done")}
              >
                View these in your profile
              </button>
            ) : (
              <Link className="AiResult__answerCont__link" to="/profile/recommendations">
                View these in your profile
              </Link>
            )}
            {cones}
          </div>
        )}

        {isModalOpened && chosenItem?.id && !sequential ? (
          <div
            className="AiResult__sortOverlay"
            onClick={() => closeModal?.()}
          >
            <div className="AiResult__sortOverlayCard" onClick={(event) => event.stopPropagation()}>
              <SortScoopCard
                career={chosenItem}
                title={careerTitle(chosenItem)}
                text={chosenItem?.description}
                rating={rating}
                onRatingChange={(value) => {
                  setRating(value);
                  handlescore?.(value);
                }}
                onDecide={applyDecision}
                dragX={0}
                dragging={false}
                exitDirection={exitDirection}
              />
              <p className="AiResult__sortOverlayHint">Tap outside the card to go back</p>
            </div>
          </div>
        ) : null}
        <Success
          image={SuccessImage}
          title="Success !"
          text="Thank you for letting us know you want to learn more! We'll take it from here based on your Career Scoops plan."
          isOpened={isSuccessModalOpened}
          close={closeSuccessModal}
        />
      </div>

      <p className="Recommendations-info">
        This page includes information from the{" "}
        <a href="https://www.onetcenter.org/database.html">O*NET 27.2 Database</a>{" "}
        by the U.S. Department of Labor, Employment and Training Administration
        (USDOL/ETA). Used under the{" "}
        <a href="https://creativecommons.org/licenses/by/4.0/">CC BY 4.0</a>{" "}
        license.&nbsp;&nbsp;Career Scoops has modified all or some of this information.
        USDOL/ETA has not approved, endorsed, or tested these modifications.
      </p>
    </>
  );
};

export default AiResult;
